import { z } from 'zod';

/**
 * Service-level (deployment-wide) plugin configuration.
 *
 * A plugin declares a small set of typed configuration fields on its server
 * definition; the host stores the operator-chosen values and exposes them on
 * the admin plugin detail page. This is the generic mechanism behind the
 * `auth-email` plugin's "allow email registration" toggle, and the first
 * consumer of it.
 *
 * Values are deployment-global (not per-org): they live in the host `config`
 * store keyed by plugin name, mirroring how feature flags and setup state are
 * persisted.
 */

/** Field value kinds a plugin can declare. Boolean-only for now; extensible. */
export const PluginServiceConfigFieldTypeSchema = z.enum(['boolean']);
export type PluginServiceConfigFieldType = z.infer<typeof PluginServiceConfigFieldTypeSchema>;

export const PluginServiceConfigFieldSchema = z.object({
  /** Stable key the value is stored under (e.g. `registrationEnabled`). */
  key: z.string().min(1),
  /** Human-readable label shown in the admin UI. */
  label: z.string().min(1),
  /** Optional help text shown beneath the control. */
  description: z.string().optional(),
  type: PluginServiceConfigFieldTypeSchema,
  /** Value used when the operator has never set this field. */
  default: z.boolean(),
});
export type PluginServiceConfigField = z.infer<typeof PluginServiceConfigFieldSchema>;

/** Map of field key -> current boolean value. */
export const PluginServiceConfigValuesSchema = z.record(z.string(), z.boolean());
export type PluginServiceConfigValues = z.infer<typeof PluginServiceConfigValuesSchema>;

/**
 * Full view returned by `GET /admin/plugins/:name/service-config`: the
 * declared fields plus their resolved values and a small audit trail.
 */
export const PluginServiceConfigViewSchema = z.object({
  pluginName: z.string(),
  fields: z.array(PluginServiceConfigFieldSchema),
  values: PluginServiceConfigValuesSchema,
  updatedAt: z.number().nullable(),
  updatedBy: z.string().nullable(),
});
export type PluginServiceConfigView = z.infer<typeof PluginServiceConfigViewSchema>;

/** Body for `PUT /admin/plugins/:name/service-config`. */
export const UpdatePluginServiceConfigRequestSchema = z.object({
  values: PluginServiceConfigValuesSchema,
});
export type UpdatePluginServiceConfigRequest = z.infer<
  typeof UpdatePluginServiceConfigRequestSchema
>;
